import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { PROVIDER_SCHEMAS, PROVIDER_TYPES } from '@/lib/providers/schema'
import {
  useValidateCredentials,
  useGetProviderEvents,
  useCreateCredential,
  useCreateIntegration,
  useSyncTiers,
  useSyncIntegration,
  useIntegrationTiers,
} from '@/lib/api/provider-queries'
import type { ProviderType, ProviderEvent, Integration } from '@/lib/api/provider-types'
import { TierMappingTable } from './TierMappingTable'
import { X, ArrowLeft, ArrowRight, Check, Loader2, AlertCircle, Search } from 'lucide-react'

interface ProviderSetupWizardProps {
  isOpen: boolean
  onClose: () => void
  eventId: number
  organizationId: number
  initialProvider?: ProviderType
}

const ALL_STEP_LABELS = ['Select Provider', 'Credentials', 'Select Event', 'Configure Tiers', 'Sync']

export function ProviderSetupWizard({ isOpen, onClose, eventId, organizationId, initialProvider }: ProviderSetupWizardProps) {
  const [step, setStep] = useState(initialProvider ? 1 : 0)
  const [selectedProvider, setSelectedProvider] = useState<ProviderType | null>(initialProvider ?? null)
  const [credentials, setCredentials] = useState<Record<string, string>>({})
  const [config, setConfig] = useState<Record<string, string>>({})
  const [credentialsValid, setCredentialsValid] = useState(false)
  const [saveCredentials, setSaveCredentials] = useState(true)
  const [providerEvents, setProviderEvents] = useState<ProviderEvent[]>([])
  const [selectedEvent, setSelectedEvent] = useState<ProviderEvent | null>(null)
  const [createdIntegration, setCreatedIntegration] = useState<Integration | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [eventSearch, setEventSearch] = useState('')

  const validateMutation = useValidateCredentials()
  const getEventsMutation = useGetProviderEvents()
  const createCredentialMutation = useCreateCredential()
  const createIntegrationMutation = useCreateIntegration()
  const syncTiersMutation = useSyncTiers()
  const syncIntegrationMutation = useSyncIntegration()
  const { data: tiers } = useIntegrationTiers(createdIntegration?.id ?? 0)

  // Reset state when wizard opens with a new provider
  useEffect(() => {
    if (isOpen) {
      setStep(initialProvider ? 1 : 0)
      setSelectedProvider(initialProvider ?? null)
      setCredentials({})
      setConfig({})
      setCredentialsValid(false)
      setSaveCredentials(true)
      setProviderEvents([])
      setSelectedEvent(null)
      setCreatedIntegration(null)
      setError(null)
      setEventSearch('')
    }
  }, [isOpen, initialProvider])

  if (!isOpen) return null

  const schema = selectedProvider ? PROVIDER_SCHEMAS[selectedProvider] : null
  const stepLabels = initialProvider ? ALL_STEP_LABELS.slice(1) : ALL_STEP_LABELS
  const displayStep = initialProvider ? step - 1 : step

  const resetWizard = () => {
    setStep(initialProvider ? 1 : 0)
    setSelectedProvider(initialProvider ?? null)
    setCredentials({})
    setConfig({})
    setCredentialsValid(false)
    setSaveCredentials(true)
    setProviderEvents([])
    setSelectedEvent(null)
    setCreatedIntegration(null)
    setError(null)
  }

  const handleClose = () => {
    resetWizard()
    onClose()
  }

  // Step 0: Select Provider
  const handleSelectProvider = (provider: ProviderType) => {
    setSelectedProvider(provider)
    setCredentials({})
    setConfig({})
    setCredentialsValid(false)
    setStep(1)
  }

  // Step 1: Validate Credentials
  const handleValidate = () => {
    if (!selectedProvider) return
    setError(null)
    validateMutation.mutate(
      { provider_type: selectedProvider, credentials },
      {
        onSuccess: (result) => {
          if (result.valid) {
            setCredentialsValid(true)
          } else {
            setError(result.message || 'Credentials are invalid')
          }
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : 'Validation failed')
        },
      }
    )
  }

  const handleCredentialsNext = () => {
    if (!selectedProvider) return
    setError(null)

    // Optionally save credentials for the org
    if (saveCredentials) {
      createCredentialMutation.mutate({
        organization: organizationId,
        provider_type: selectedProvider,
        credentials,
      })
    }

    // Fetch provider events
    getEventsMutation.mutate(
      {
        provider_type: selectedProvider,
        organization_id: organizationId,
        credentials,
        config: Object.keys(config).length > 0 ? config : undefined,
      },
      {
        onSuccess: (events) => {
          setProviderEvents(events)
          setStep(2)
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : 'Failed to load provider events')
        },
      }
    )
  }

  // Step 2: Select Event -> Create Integration
  const handleSelectEvent = (providerEvent: ProviderEvent) => {
    setSelectedEvent(providerEvent)
  }

  const handleEventNext = () => {
    if (!selectedProvider || !selectedEvent) return
    setError(null)

    createIntegrationMutation.mutate(
      {
        event: eventId,
        provider_type: selectedProvider,
        external_event_id: selectedEvent.id,
        external_event_name: selectedEvent.name,
        is_enabled: true,
        config: Object.keys(config).length > 0 ? config : undefined,
      },
      {
        onSuccess: (integration) => {
          setCreatedIntegration(integration)
          // Auto sync tiers
          syncTiersMutation.mutate(integration.id, {
            onSuccess: () => setStep(3),
            onError: () => setStep(3), // Still advance, tiers can be synced later
          })
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : 'Failed to create integration')
        },
      }
    )
  }

  // Step 4: Sync
  const handleStartSync = () => {
    if (!createdIntegration) return
    setError(null)
    syncIntegrationMutation.mutate(createdIntegration.id, {
      onError: (err) => {
        setError(err instanceof Error ? err.message : 'Sync failed')
      },
    })
  }


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-neutral-950 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-800">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
              {schema ? `Connect ${schema.label}` : 'Add Ticketing Provider'}
            </h2>
            <div className="flex items-center gap-2 mt-2">
              {stepLabels.map((label, i) => (
                <div key={label} className="flex items-center gap-1">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    i < displayStep
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                      : i === displayStep
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
                        : 'bg-neutral-100 text-neutral-400 dark:bg-neutral-800 dark:text-neutral-500'
                  }`}>
                    {i < displayStep ? <Check className="w-3 h-3" /> : i + 1}
                  </div>
                  {i < stepLabels.length - 1 && (
                    <div className={`w-6 h-px ${i < displayStep ? 'bg-green-300 dark:bg-green-700' : 'bg-neutral-200 dark:bg-neutral-700'}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
          <button type="button" onClick={handleClose} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Step 0: Select Provider */}
          {step === 0 && (
            <div className="grid gap-3 sm:grid-cols-3">
              {PROVIDER_TYPES.map(type => {
                const s = PROVIDER_SCHEMAS[type]
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleSelectProvider(type)}
                    className="p-4 rounded-lg border border-neutral-200 dark:border-neutral-800 hover:border-blue-400 dark:hover:border-blue-600 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors text-left"
                  >
                    <div className="font-medium text-neutral-900 dark:text-white">{s.label}</div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{s.description}</p>
                  </button>
                )
              })}
            </div>
          )}

          {/* Step 1: Credentials */}
          {step === 1 && schema && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                Enter your {schema.label} credentials to connect.
              </p>

              {schema.credentials.map(field => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  <Input
                    type={field.secret ? 'password' : 'text'}
                    placeholder={field.placeholder}
                    value={credentials[field.key] ?? ''}
                    onChange={e => {
                      setCredentials(prev => ({ ...prev, [field.key]: e.target.value }))
                      setCredentialsValid(false)
                    }}
                  />
                </div>
              ))}

              {schema.config.map(field => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  <Input
                    placeholder={field.placeholder}
                    value={config[field.key] ?? ''}
                    onChange={e => setConfig(prev => ({ ...prev, [field.key]: e.target.value }))}
                  />
                </div>
              ))}

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="save-creds"
                  checked={saveCredentials}
                  onChange={e => setSaveCredentials(e.target.checked)}
                  className="rounded border-neutral-300 dark:border-neutral-600"
                />
                <label htmlFor="save-creds" className="text-sm text-neutral-600 dark:text-neutral-400">
                  Save credentials for this organization
                </label>
              </div>

              {credentialsValid && (
                <div className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400">
                  <Check className="w-4 h-4" />
                  Credentials validated
                </div>
              )}

              <div className="flex items-center gap-2 pt-2">
                {!initialProvider && (
                  <Button type="button" variant="outline" onClick={() => setStep(0)}>
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back
                  </Button>
                )}
                {!credentialsValid ? (
                  <Button type="button" onClick={handleValidate} disabled={validateMutation.isPending}>
                    {validateMutation.isPending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                    Validate
                  </Button>
                ) : (
                  <Button type="button" onClick={handleCredentialsNext} disabled={getEventsMutation.isPending}>
                    {getEventsMutation.isPending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                    Next
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
                {selectedProvider === 'ticketlabs' && (
                  <a
                    href="https://ticketlabs.com/home"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto inline-flex items-center justify-center rounded-md text-sm font-medium h-9 px-4 py-2 border border-blue-300 text-blue-600 bg-transparent hover:bg-blue-50 hover:text-blue-700 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-300 transition-colors"
                  >
                    Sign Up
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Select Event */}
          {step === 2 && (() => {
            const now = new Date()
            const searchLower = eventSearch.toLowerCase()
            const filtered = providerEvents.filter(pe =>
              !eventSearch || pe.name.toLowerCase().includes(searchLower)
            )
            const upcoming = filtered
              .filter(pe => pe.start_date && new Date(pe.start_date) >= now)
              .sort((a, b) => new Date(a.start_date!).getTime() - new Date(b.start_date!).getTime())
            const past = filtered
              .filter(pe => !pe.start_date || new Date(pe.start_date) < now)
              .sort((a, b) => new Date(b.start_date || 0).getTime() - new Date(a.start_date || 0).getTime())

            const renderEventButton = (pe: ProviderEvent) => (
              <button
                key={pe.id}
                type="button"
                onClick={() => handleSelectEvent(pe)}
                className={`w-full p-3 rounded-lg border text-left transition-colors ${
                  selectedEvent?.id === pe.id
                    ? 'border-blue-400 bg-blue-50 dark:border-blue-600 dark:bg-blue-900/20'
                    : 'border-neutral-200 dark:border-neutral-800 hover:border-neutral-300 dark:hover:border-neutral-700'
                }`}
              >
                <div className="font-medium text-neutral-900 dark:text-white">{pe.name}</div>
                {pe.start_date && (
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                    {new Date(pe.start_date).toLocaleDateString()}
                    {pe.venue && ` - ${pe.venue}`}
                  </div>
                )}
              </button>
            )

            return (
            <div className="space-y-4">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                Select the event to link to this EventPay event.
              </p>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input
                  placeholder="Search events..."
                  value={eventSearch}
                  onChange={e => setEventSearch(e.target.value)}
                  className="pl-9"
                />
              </div>

              {providerEvents.length === 0 ? (
                <p className="text-sm text-neutral-500 py-4 text-center">No events found from provider.</p>
              ) : filtered.length === 0 ? (
                <p className="text-sm text-neutral-500 py-4 text-center">No events match your search.</p>
              ) : (
                <div className="max-h-80 overflow-y-auto space-y-4">
                  {upcoming.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">Upcoming Events</h4>
                      <div className="space-y-2">
                        {upcoming.map(renderEventButton)}
                      </div>
                    </div>
                  )}
                  {past.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">Past Events</h4>
                      <div className="space-y-2">
                        {past.map(renderEventButton)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setStep(1)}>
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={handleEventNext}
                  disabled={!selectedEvent || createIntegrationMutation.isPending}
                >
                  {(createIntegrationMutation.isPending || syncTiersMutation.isPending) && (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  )}
                  Create Integration
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
            )
          })()}

          {/* Step 3: Configure Tiers */}
          {step === 3 && createdIntegration && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                Configure how each ticket tier maps to EventPay tokens and vouchers.
              </p>

              {syncTiersMutation.isPending ? (
                <div className="flex items-center justify-center py-8 text-neutral-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Syncing tiers from provider...
                </div>
              ) : (
                <TierMappingTable
                  integrationId={createdIntegration.id}
                  tiers={tiers ?? []}
                  eventId={eventId}
                />
              )}

              <div className="flex items-center gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setStep(2)} disabled>
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
                <Button type="button" onClick={() => setStep(4)}>
                  Next
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Sync */}
          {step === 4 && createdIntegration && (
            <div className="space-y-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-neutral-500 dark:text-neutral-400">Provider</span>
                      <span className="font-medium text-neutral-900 dark:text-white">
                        {schema?.label}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-500 dark:text-neutral-400">External Event</span>
                      <span className="font-medium text-neutral-900 dark:text-white">
                        {createdIntegration.external_event_name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-500 dark:text-neutral-400">Tiers Configured</span>
                      <span className="font-medium text-neutral-900 dark:text-white">
                        {tiers?.length ?? 0}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {syncIntegrationMutation.isSuccess && (
                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-1.5">
                    <Check className="w-4 h-4" />
                    Sync started successfully. Tickets will appear once processing completes.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-2 pt-2">
                {!syncIntegrationMutation.isSuccess ? (
                  <>
                    <Button type="button" variant="outline" onClick={() => setStep(3)}>
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={handleStartSync}
                      disabled={syncIntegrationMutation.isPending}
                    >
                      {syncIntegrationMutation.isPending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                      Start Initial Sync
                    </Button>
                    <Button type="button" variant="ghost" onClick={handleClose}>
                      Skip & Close
                    </Button>
                  </>
                ) : (
                  <Button type="button" onClick={handleClose}>
                    Done
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
